/**
 * TrainingHistoryPage - Historical Training Runs View
 * 
 * Platform admin page for viewing training job history and logs.
 * 
 * Following BRAIN-PRE-CODE-CHECKLIST.md:
 * - Clean single imports
 * - Defensive null checks
 * - Proper error handling
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Box, FormControl, Select, MenuItem, InputLabel, Alert, CircularProgress } from '@mui/material';
import { PageHeader } from './shared/components';
import { TrainingHistory } from './components/Training/TrainingHistory';
import { TrainingLogViewer } from './components/Training/TrainingLogViewer';
import { mlApiClient } from './shared/utils';

interface TrainingRun {
  run_id: string;
  tenant_id: string;
  start_time: string;
  end_time: string;
  duration_seconds: number;
  status: 'completed' | 'failed' | 'cancelled';
  models_trained: number;
  success_rate: number;
  error_message: string | null;
}

interface TrainingHistoryResponse {
  total: number;
  items: TrainingRun[];
  offset: number;
  limit: number;
}

interface Tenant {
  id: string;
  name: string;
  status: string;
}

export const TrainingHistoryPage: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [runs, setRuns] = useState<TrainingRun[]>([]);
  const [selectedRun, setSelectedRun] = useState<TrainingRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  // ML-011: Load tenants dynamically from backend
  useEffect(() => {
    const loadTenants = async () => {
      setLoadingTenants(true);
      try {
        const response = await fetch('/api/platform-admin/tenants', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          const tenantList = data.tenants || [];
          setTenants(tenantList);
          
          // Set default tenant to first in list, or AstraPeak if it exists
          if (tenantList.length > 0) {
            const astrapeakTenant = tenantList.find(
              (t: Tenant) => t.id === 'd6f953f4-d914-4906-b61e-d48d6b975f74'
            );
            setSelectedTenant(astrapeakTenant ? astrapeakTenant.id : tenantList[0].id);
          }
        } else {
          console.error('Failed to load tenants:', response.statusText);
        }
      } catch (error) {
        console.error('Error loading tenants:', error);
      } finally {
        setLoadingTenants(false);
      }
    };
    
    loadTenants();
  }, []);

  const loadTrainingHistory = useCallback(async () => {
    if (!selectedTenant) return; // Don't load until tenant is selected
    
    try {
      setLoading(true);
      setError(null);
      const data = await mlApiClient.getTrainingHistory(selectedTenant);
      // Handle new pagination response structure
      if (data && typeof data === 'object' && 'items' in data) {
        const response = data as TrainingHistoryResponse;
        setRuns(response.items);
        setTotal(response.total || 0);
      } else {
        // Fallback for old response format
        const runs = Array.isArray(data) ? data as TrainingRun[] : [];
        setRuns(runs);
        setTotal(runs.length);
      }
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to load training history');
    } finally {
      setLoading(false);
    }
  }, [selectedTenant]);

  useEffect(() => {
    loadTrainingHistory();
  }, [loadTrainingHistory]);

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Training History"
        subtitle="View historical training runs and logs"
        onRefresh={loadTrainingHistory}
        loading={loading}
      />

      {/* Tenant Selector */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 250 }}>
          <InputLabel>Tenant</InputLabel>
          <Select
            value={selectedTenant}
            onChange={(e) => setSelectedTenant(e.target.value)}
            label="Tenant"
            disabled={loadingTenants}
          >
            {loadingTenants ? (
              <MenuItem value="" disabled>Loading tenants...</MenuItem>
            ) : tenants.length === 0 ? (
              <MenuItem value="" disabled>No tenants available</MenuItem>
            ) : (
              tenants.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
        
        {!loading && total > 0 && (
          <Box sx={{ color: 'text.secondary' }}>
            Showing {runs.length} of {total} training runs
          </Box>
        )}
      </Box>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Training History Table */}
      {!loading && !error && (
        <TrainingHistory
          runs={runs}
          onSelectRun={setSelectedRun}
        />
      )}

      {/* Training Log Viewer Modal */}
      {selectedRun && (
        <TrainingLogViewer
          runId={selectedRun.run_id}
          onClose={() => setSelectedRun(null)}
        />
      )}
    </Box>
  );
};

export default TrainingHistoryPage;
